import { useState, useEffect } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import FilterModal from '../FilterModal';
import ExperienceScopeSelector, { ExperienceScope } from './ExperienceScopeSelector';
import ExperienceOperatorToggle from './ExperienceOperatorToggle';
import MinHoursSelector, { countAtLevel, stickyControls } from './MinHoursSelector';
import { useSearchClientsQuery } from 'utils/wemble-api';

interface SelectedClient {
  key: string;
  label: string;
  countryCode?: string;
}

/** Highlight matching text within a string */
const HighlightMatch = ({ text, query }: { text: string; query: string }) => {
  if (!query || query.length < 2) return <>{text}</>;
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <span style={{ backgroundColor: 'rgba(107, 142, 107, 0.15)', borderRadius: 2, padding: '0 1px' }}>
        {text.slice(index, index + query.length)}
      </span>
      {text.slice(index + query.length)}
    </>
  );
};

interface Props {
  visible: boolean;
  onClose: () => void;
  onSave: (keys: string[], labels: string[], scope: ExperienceScope, operator: 'and' | 'or', minHours: number) => void;
  initialSelected: SelectedClient[];
  initialScope?: ExperienceScope;
  initialOperator?: 'and' | 'or';
  /** Min. hours of the applied filter; 0 = any */
  initialMinHours?: number;
  /** A client's people counts (from the experience facets), for the min-hours steps */
  countsFor?: (key: string) => { count: number; countAtLeast?: number[] } | undefined;
  onScopeChange?: (scope: ExperienceScope) => void;
  teams?: string;
  titles?: string;
}

const ClientSearchModal = ({ visible, onClose, onSave, initialSelected, initialScope = 'pastYear', initialOperator = 'and', initialMinHours = 0, countsFor, onScopeChange, teams, titles }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selected, setSelected] = useState<SelectedClient[]>(initialSelected);
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<ExperienceScope>(initialScope);
  const [operator, setOperator] = useState<'and' | 'or'>(initialOperator);
  const [minHours, setMinHours] = useState(initialMinHours);

  useEffect(() => {
    if (visible) {
      setSelected(initialSelected);
      setSearch('');
      setMinHours(initialMinHours);
    }
  }, [visible]);

  const handleScopeChange = (s: ExperienceScope) => {
    setScope(s);
    onScopeChange?.(s);
  };

  // Only search when 3+ characters typed
  const { data: searchResults } = useSearchClientsQuery(
    { q: search, scope, limit: 20, teams, titles },
    { skip: search.length < 3 },
  );

  const toggle = (key: string, label: string, countryCode?: string) => {
    setSelected(prev =>
      prev.some(s => s.key === key)
        ? prev.filter(s => s.key !== key)
        : [...prev, { key, label, countryCode }]
    );
  };

  const handleSave = () => {
    onSave(selected.map(s => s.key), selected.map(s => s.label), scope, operator, minHours);
    onClose();
  };

  // Only show search results when actively searching (3+ chars)
  const selectedKeys = new Set(selected.map(s => s.key));
  const results = search.length >= 3 ? (searchResults || []).filter(r => !selectedKeys.has(r.key) && countAtLevel(r, minHours) > 0) : [];

  return (
    <FilterModal
      visible={visible}
      onClose={onClose}
      onSave={handleSave}
      title="Client Experience"
      description="Find people who have worked for a specific client"
    >
      <div>
        <div style={stickyControls(isMobile)}>
        <ExperienceScopeSelector value={scope} onChange={handleScopeChange} />
        <MinHoursSelector
          value={minHours}
          onChange={setMinHours}
          counts={selected.length === 1 ? countsFor?.(selected[0].key) : undefined}
        />
        <ExperienceOperatorToggle value={operator} onChange={setOperator} selectedCount={selected.length} />

        <input
          autoFocus={!isMobile}
          type="text"
          placeholder="Search clients..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 8,
            border: '1px solid #e0e0e0', fontSize: 13, marginBottom: 8,
            outline: 'none', boxSizing: 'border-box',
          }}
        />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, minHeight: 18 }}>
          {selected.length > 0 ? (
            <>
              <span style={{ fontSize: 12, color: '#999' }}>{selected.length} selected</span>
              <button
                onClick={() => setSelected([])}
                style={{ fontSize: 12, color: '#6B8E6B', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                Clear all
              </button>
            </>
          ) : (
            <span style={{ fontSize: 12, color: '#ccc' }}>No clients selected</span>
          )}
        </div>

        {/* Selected clients always shown on top */}
        {selected.map(client => (
          <div
            key={client.key}
            onClick={() => toggle(client.key, client.label)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
              backgroundColor: '#EDF3ED',
              userSelect: 'none', WebkitUserSelect: 'none' as any,
              marginBottom: 1,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 18, height: 18, borderRadius: 4, border: 'none',
                backgroundColor: '#6B8E6B',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, color: 'white', fontWeight: 700, flexShrink: 0,
              }}>✓</div>
              <span style={{ fontSize: 14, color: '#333' }}>{client.label}</span>
              {client.countryCode && (
                <span className={`fi fi-${client.countryCode.toLowerCase()}`}
                  style={{ width: 14, height: 10, borderRadius: 1, boxShadow: '0 0 0 0.5px rgba(0,0,0,0.15)', flexShrink: 0 }} />
              )}
            </div>
          </div>
        ))}

        {/* Search results */}
        {results.map(option => (
          <div
            key={option.key}
            onClick={() => toggle(option.key, option.label, option.countryCode)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
              backgroundColor: 'transparent',
              userSelect: 'none', WebkitUserSelect: 'none' as any,
              transition: 'background-color 0.1s',
              marginBottom: 1,
            }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#FAFAFA'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 18, height: 18, borderRadius: 4,
                border: '2px solid #ccc', backgroundColor: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, color: 'white', fontWeight: 700, flexShrink: 0,
              }} />
              <span style={{ fontSize: 14, color: '#333' }}><HighlightMatch text={option.label} query={search} /></span>
              {option.countryCode && (
                <span className={`fi fi-${option.countryCode.toLowerCase()}`}
                  style={{ width: 14, height: 10, borderRadius: 1, boxShadow: '0 0 0 0.5px rgba(0,0,0,0.15)', flexShrink: 0 }} />
              )}
            </div>
            <span style={{ fontSize: 12, color: '#999' }}>{countAtLevel(option, minHours)}</span>
          </div>
        ))}

        {search.length >= 3 && results.length === 0 && selected.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>
            No clients match "{search}"
          </div>
        )}

        {search.length < 3 && selected.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#ccc', fontSize: 13 }}>
            Type at least 3 characters to search
          </div>
        )}
      </div>
    </FilterModal>
  );
};

export default ClientSearchModal;
